import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { TESTIMONIALS } from "../data/siteContent";

export default function Testimonials() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % TESTIMONIALS.length), 6500);
    return () => clearInterval(t);
  }, []);

  const current = TESTIMONIALS[index];
  const initials = current.couple
    .split("&")
    .map((s) => s.trim()[0])
    .join(" & ");

  return (
    <section data-testid="testimonials-section" className="py-20 sm:py-28 lg:py-32 relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Kind Words"
          title={
            <>
              Couples who became <span className="italic text-gold">family</span>
            </>
          }
        />

        <Reveal>
          <div className="relative border border-white/[0.07] bg-ink-soft/50 px-6 sm:px-12 lg:px-16 py-12 sm:py-14 text-center" data-testid="testimonials-carousel">
            <Quote className="w-10 h-10 text-gold/40 mx-auto mb-6" strokeWidth={1.2} />

            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.6, ease: EASE }}
              >
                <div className="flex justify-center gap-1.5 mb-6" data-testid="testimonial-stars">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 text-gold" fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote
                  data-testid="testimonial-quote"
                  className="font-display text-xl sm:text-2xl lg:text-[1.7rem] italic text-ivory leading-snug mb-8"
                >
                  “{current.quote}”
                </blockquote>
                <div className="flex items-center justify-center gap-4">
                  <span className="w-11 h-11 rounded-full border border-gold/50 bg-ink-elev flex items-center justify-center font-display text-gold text-lg">
                    {initials}
                  </span>
                  <div className="text-left">
                    <p className="text-ivory font-medium text-sm tracking-wide" data-testid="testimonial-couple">
                      {current.couple}
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-ivory-mute mt-0.5">
                      Wedding in {current.venue}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-center gap-6 mt-10">
              <button
                data-testid="testimonial-prev"
                aria-label="Previous testimonial"
                onClick={() => setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                className="p-2.5 border border-white/15 text-ivory-dim hover:border-gold hover:text-gold transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex gap-2.5">
                {TESTIMONIALS.map((_, d) => (
                  <button
                    key={d}
                    data-testid={`testimonial-dot-${d}`}
                    aria-label={`Testimonial ${d + 1}`}
                    onClick={() => setIndex(d)}
                    className={`h-1.5 transition-all duration-400 ${
                      d === index ? "w-8 bg-gold" : "w-1.5 bg-ivory/25 hover:bg-ivory/50"
                    }`}
                  />
                ))}
              </div>
              <button
                data-testid="testimonial-next"
                aria-label="Next testimonial"
                onClick={() => setIndex((i) => (i + 1) % TESTIMONIALS.length)}
                className="p-2.5 border border-white/15 text-ivory-dim hover:border-gold hover:text-gold transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
